import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Navigation, useAuth, ProtectedRoute } from '../App';
import { MessageSquare, Plus, ChevronRight, ArrowLeft } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function CommunityPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [threads, setThreads] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [showNewThread, setShowNewThread] = useState(false);
  const [newThread, setNewThread] = useState({ title: '', content: '', category_id: '' });

  const handleBack = () => {
    if (window.history.length > 1) {
      window.history.back();
    } else {
      navigate('/');
    }
  };

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/forum/categories`);
        if (res.ok) setCategories(await res.json());
      } catch (error) {
        console.error('Error fetching categories:', error);
      }
    };
    fetchCategories();
  }, []);

  useEffect(() => {
    const fetchThreads = async () => {
      try {
        const url = selectedCategory 
          ? `${BACKEND_URL}/api/forum/threads?category_id=${selectedCategory}`
          : `${BACKEND_URL}/api/forum/threads`;
        const res = await fetch(url);
        if (res.ok) {
          const data = await res.json();
          setThreads(data.threads || []);
        }
      } catch (error) {
        console.error('Error fetching threads:', error);
      }
    };
    fetchThreads();
  }, [selectedCategory]);

  const handleCreateThread = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`${BACKEND_URL}/api/forum/threads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(newThread)
      });
      
      if (res.ok) {
        setShowNewThread(false);
        setNewThread({ title: '', content: '', category_id: '' });
        // Refresh threads
        const threadsRes = await fetch(
          selectedCategory 
            ? `${BACKEND_URL}/api/forum/threads?category_id=${selectedCategory}`
            : `${BACKEND_URL}/api/forum/threads`
        );
        if (threadsRes.ok) {
          const data = await threadsRes.json();
          setThreads(data.threads || []);
        }
      }
    } catch (error) {
      console.error('Error creating thread:', error);
    }
  };

  return (
    <ProtectedRoute>
      <div className="min-h-screen bg-background">
        <Navigation />
        
        <div className="p-4 md:p-8">
          <div className="max-w-6xl mx-auto">
          {/* Back Button */}
          <button
            onClick={handleBack}
            className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-6 group"
            data-testid="back-button"
          >
            <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
            <span className="text-sm font-medium">Back</span>
          </button>

          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-heading font-bold mb-2">Community Forum</h1>
              <p className="text-muted-foreground">Ask questions, share hands, and discuss poker strategy</p>
            </div>
            <button
              onClick={() => setShowNewThread(!showNewThread)}
              className="bg-primary text-primary-foreground px-6 py-3 rounded-lg font-semibold hover:bg-primary/90 transition-colors flex items-center gap-2"
              data-testid="forum-thread-create-button"
            >
              <Plus className="w-5 h-5" />
              New Thread
            </button>
          </div>

          {/* New Thread Form */}
          {showNewThread && (
            <div className="bg-card border border-border rounded-xl p-6 shadow-lg mb-6">
              <h2 className="text-xl font-heading font-bold mb-4">Create New Thread</h2>
              <form onSubmit={handleCreateThread} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Category</label>
                  <select
                    value={newThread.category_id}
                    onChange={(e) => setNewThread({ ...newThread, category_id: e.target.value })}
                    className="w-full bg-background border border-border rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
                    required
                  >
                    <option value="">Select a category</option>
                    {categories.map(cat => (
                      <option key={cat.category_id} value={cat.category_id}>{cat.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Title</label>
                  <input
                    type="text"
                    value={newThread.title}
                    onChange={(e) => setNewThread({ ...newThread, title: e.target.value })}
                    className="w-full bg-background border border-border rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="Give your thread a clear title"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Content</label>
                  <textarea
                    value={newThread.content}
                    onChange={(e) => setNewThread({ ...newThread, content: e.target.value })}
                    className="w-full bg-background border border-border rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-primary min-h-[150px]"
                    placeholder="Share your question, hand, or discussion topic..."
                    required
                  />
                </div>
                <div className="flex gap-3">
                  <button
                    type="submit"
                    className="bg-primary text-primary-foreground px-6 py-2 rounded-lg font-semibold hover:bg-primary/90 transition-colors"
                  >
                    Post Thread
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowNewThread(false)}
                    className="bg-secondary text-secondary-foreground px-6 py-2 rounded-lg font-semibold hover:bg-secondary/80 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          )}

          <div className="grid lg:grid-cols-4 gap-6">
            {/* Categories Sidebar */}
            <div className="lg:col-span-1">
              <div className="bg-card border border-border rounded-xl p-4 shadow-lg">
                <h2 className="text-lg font-heading font-bold mb-4">Categories</h2>
                <div className="space-y-2">
                  <button
                    onClick={() => setSelectedCategory(null)}
                    className={`w-full text-left px-3 py-2 rounded-lg transition-colors ${!selectedCategory ? 'bg-primary/10 text-primary font-medium' : 'hover:bg-white/5'}`}
                  >
                    All Threads
                  </button>
                  {categories.map(cat => (
                    <button
                      key={cat.category_id}
                      onClick={() => setSelectedCategory(cat.category_id)}
                      className={`w-full text-left px-3 py-2 rounded-lg transition-colors ${selectedCategory === cat.category_id ? 'bg-primary/10 text-primary font-medium' : 'hover:bg-white/5'}`}
                    >
                      {cat.name}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Threads List */}
            <div className="lg:col-span-3">
              <div className="space-y-4">
                {threads.length === 0 ? (
                  <div className="bg-card border border-border rounded-xl p-12 text-center">
                    <MessageSquare className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                    <h3 className="text-xl font-heading font-bold mb-2">No threads yet</h3>
                    <p className="text-muted-foreground mb-6">Be the first to start a discussion!</p>
                    <button
                      onClick={() => setShowNewThread(true)}
                      className="bg-primary text-primary-foreground px-6 py-3 rounded-lg font-semibold hover:bg-primary/90 transition-colors inline-flex items-center gap-2"
                    >
                      <Plus className="w-5 h-5" />
                      Create First Thread
                    </button>
                  </div>
                ) : (
                  threads.map(thread => (
                    <Link
                      key={thread.thread_id}
                      to={`/community/thread/${thread.thread_id}`}
                      className="block bg-card border border-border rounded-xl p-6 hover:shadow-lg transition-all"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h3 className="text-lg font-heading font-semibold mb-2 hover:text-primary transition-colors">
                            {thread.title}
                          </h3>
                          <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
                            {thread.content}
                          </p>
                          <div className="flex items-center gap-4 text-xs text-muted-foreground">
                            <div className="flex items-center gap-2">
                              <img src={thread.author_picture} alt="" className="w-5 h-5 rounded-full" />
                              <span>{thread.author_name}</span>
                            </div>
                            <span>•</span>
                            <span>{new Date(thread.created_at).toLocaleDateString()}</span>
                            <span>•</span>
                            <span>{thread.reply_count} replies</span>
                          </div>
                        </div>
                        <ChevronRight className="w-5 h-5 text-muted-foreground flex-shrink-0 ml-4" />
                      </div>
                    </Link>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      </div>
    </ProtectedRoute>
  );
}
